/**
 * The reference model you build around.
 *
 * Uses three's GLTFLoader (GLB and .gltf with embedded buffers), which replaces
 * the hand-rolled reader the r128 build carried — see `docs/ROADMAP.md`. Its
 * meshes optionally join the collider set so standards can stand on roofs and
 * decks of the imported building.
 *
 * The model keeps the world coordinates it was exported at. That alignment is
 * the whole point: a scaffold built against it is authored in the same frame,
 * so exporting the scaffold on its own drops it back into the source model at
 * the right place. Recentring is available but off by default, and it forfeits
 * that alignment.
 */

import { Box3, Group, type Material, Mesh, type Object3D, Vector3 } from "three";
import { GLTFLoader } from "three/addons/loaders/GLTFLoader.js";
import { fail, ok, type Result } from "../lib/result.ts";

export type ReferenceInfo = Readonly<{
  name: string;
  meshCount: number;
  size: Readonly<{ x: number; y: number; z: number }>;
  /** Bounding-box centre in world metres — X/Z of the footprint, Y of its base. */
  origin: Readonly<{ x: number; y: number; z: number }>;
  centred: boolean;
}>;

export type ImportOptions = Readonly<{
  /** Metres per file unit. */
  scale: number;
  /**
   * Move the model to the origin and set it on the ground. Off by default —
   * it discards the exported placement, so a scaffold built against the model
   * no longer lines up with the source file.
   */
  centre: boolean;
}>;

export class ReferenceModel {
  readonly group = new Group();

  private meshes: Mesh[] = [];
  private box: Box3 | null = null;
  private readonly loader = new GLTFLoader();

  get colliderMeshes(): Object3D[] {
    return this.meshes;
  }

  get bounds(): Box3 | null {
    return this.box;
  }

  /** Parse and place a dropped/selected file. Never throws — errors come back as a Result. */
  async load(file: File, options: ImportOptions): Promise<Result<ReferenceInfo>> {
    let buffer: ArrayBuffer;
    try {
      buffer = await file.arrayBuffer();
    } catch (err) {
      return fail("Could not read the file", err);
    }

    try {
      const gltf = await this.loader.parseAsync(buffer, "");
      return ok(this.place(gltf.scene, file.name, options));
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      return fail(`Could not read the model: ${msg}`, err);
    }
  }

  private place(scene: Group, name: string, options: ImportOptions): ReferenceInfo {
    this.clear();

    scene.scale.setScalar(options.scale);
    scene.updateMatrixWorld(true);

    if (options.centre) {
      const box = new Box3().setFromObject(scene);
      const centre = box.getCenter(new Vector3());
      scene.position.set(-centre.x, -box.min.y, -centre.z);
      scene.updateMatrixWorld(true);
    }

    scene.traverse((object) => {
      if (!(object instanceof Mesh)) {
        return;
      }

      object.castShadow = true;
      object.receiveShadow = true;
      this.meshes.push(object);
    });

    this.group.add(scene);
    this.box = new Box3().setFromObject(this.group);
    const size = this.box.getSize(new Vector3());
    const centre = this.box.getCenter(new Vector3());

    return {
      name,
      meshCount: this.meshes.length,
      size: { x: size.x, y: size.y, z: size.z },
      origin: { x: centre.x, y: this.box.min.y, z: centre.z },
      centred: options.centre,
    };
  }

  /** Fade the model back so the scaffold reads in front of it. */
  setOpacity(opacity: number): void {
    const solid = opacity >= 0.995;

    for (const material of this.materials()) {
      material.transparent = !solid;
      material.opacity = solid ? 1 : opacity;
      material.depthWrite = solid || opacity > 0.95;
      material.needsUpdate = true;
    }
  }

  private materials(): Material[] {
    const unique = new Set<Material>();

    for (const mesh of this.meshes) {
      for (const material of Array.isArray(mesh.material) ? mesh.material : [mesh.material]) {
        unique.add(material);
      }
    }

    return [...unique];
  }

  clear(): void {
    for (const material of this.materials()) {
      material.dispose();
    }
    for (const mesh of this.meshes) {
      mesh.geometry.dispose();
    }

    this.group.clear();
    this.meshes = [];
    this.box = null;
  }
}
